'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  approveBusinessGovernanceOwnership,
  createBusinessGovernanceOwnership,
  getBusinessGovernanceIdentities,
  getBusinessGovernanceOwnershipHistory,
  getBusinessGovernanceOwnershipRecords,
  rejectBusinessGovernanceOwnership,
  setBusinessGovernanceOwnershipActive,
  submitBusinessGovernanceOwnership,
  updateBusinessGovernanceOwnership,
  type BusinessGovernanceIdentity,
  type BusinessGovernanceOwnerType,
  type BusinessGovernanceOwnership,
  type BusinessGovernanceOwnershipHistoryEvent,
  type BusinessGovernanceOwnershipInput,
  type BusinessGovernanceResponsibilityType,
  type BusinessGovernanceRetentionRule,
  type BusinessGovernanceScopeType,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';
import {
  cleanSpaces,
  dateTime,
  errorMessage,
  FinanceField,
  FinanceModal,
  FinanceStatus,
  label,
} from './BusinessReportFinanceUi';

type OwnershipForm = {
  recordKey: string;
  responsibilityType: BusinessGovernanceResponsibilityType;
  title: string;
  description: string;
  ownerType: BusinessGovernanceOwnerType;
  ownerUsername: string;
  ownerTeam: string;
  ownerContact: string;
  primaryValidatorUsername: string;
  backupValidatorUsername: string;
  makerUsername: string;
  checkerUsername: string;
  scopeType: BusinessGovernanceScopeType;
  scopeReference: string;
  effectiveFrom: string;
  effectiveTo: string;
  retentionRules: BusinessGovernanceRetentionRule[];
};

type DecisionDialog =
  | { kind: 'approve'; record: BusinessGovernanceOwnership; reference: string; notes: string }
  | { kind: 'reject'; record: BusinessGovernanceOwnership; reason: string };

const EMPTY_FORM: OwnershipForm = {
  recordKey: '',
  responsibilityType: 'OTHER',
  title: '',
  description: '',
  ownerType: 'TEAM',
  ownerUsername: '',
  ownerTeam: '',
  ownerContact: '',
  primaryValidatorUsername: '',
  backupValidatorUsername: '',
  makerUsername: '',
  checkerUsername: '',
  scopeType: 'ORGANIZATION',
  scopeReference: '',
  effectiveFrom: '',
  effectiveTo: '',
  retentionRules: [],
};

const RESPONSIBILITY_TYPES: BusinessGovernanceResponsibilityType[] = [
  'BUSINESS_DEFINITION_OWNER',
  'DEFINITION_VALIDATOR',
  'DATA_SOURCE_OWNER',
  'ACCESS_GOVERNANCE',
  'REPORT_SCOPE',
  'RETENTION_POLICY',
  'OTHER',
];
const SCOPE_TYPES: BusinessGovernanceScopeType[] = [
  'ORGANIZATION',
  'CENTRE',
  'REPORT',
  'SOURCE',
];

export function BusinessGovernanceOwnershipTab({
  active,
  token,
  userName,
}: {
  active: boolean;
  token?: string;
  userName: string;
}) {
  const [records, setRecords] = useState<BusinessGovernanceOwnership[]>([]);
  const [identities, setIdentities] = useState<BusinessGovernanceIdentity[]>([]);
  const [editing, setEditing] = useState<BusinessGovernanceOwnership | null | undefined>(undefined);
  const [form, setForm] = useState<OwnershipForm>(EMPTY_FORM);
  const [decision, setDecision] = useState<DecisionDialog | null>(null);
  const [historyRecord, setHistoryRecord] = useState<BusinessGovernanceOwnership | null>(null);
  const [history, setHistory] = useState<BusinessGovernanceOwnershipHistoryEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ownershipRows, identityRows] = await Promise.all([
        getBusinessGovernanceOwnershipRecords(token),
        getBusinessGovernanceIdentities(token),
      ]);
      setRecords(ownershipRows);
      setIdentities(identityRows);
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load Governance & Ownership', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  const makerIdentities = useMemo(
    () => identities.filter((identity) => identity.roles.includes('BUSINESS_REPORTS_DATA_ENTRY')),
    [identities],
  );
  const checkerIdentities = useMemo(
    () => identities.filter((identity) => identity.roles.includes('BUSINESS_REPORTS_FINANCE')),
    [identities],
  );
  const canCreate = makerIdentities.some((identity) => sameIdentity(identity.username, userName));
  const counts = useMemo(() => ({
    total: records.length,
    pending: records.filter((record) => record.status === 'PENDING_APPROVAL').length,
    approved: records.filter((record) => record.status === 'APPROVED').length,
    active: records.filter((record) => record.isActive).length,
  }), [records]);
  const businessDefinitionOwner = useMemo(
    () => records.find((record) => record.recordKey === 'BR-0-01'),
    [records],
  );
  const businessDefinitionOwnerIsAuthoritative = Boolean(
    businessDefinitionOwner?.status === 'APPROVED'
      && businessDefinitionOwner.isActive
      && businessDefinitionOwner.approvalReference,
  );

  if (!active) return null;

  const openEditor = (record: BusinessGovernanceOwnership | null) => {
    if (record) {
      setForm({
        recordKey: record.recordKey,
        responsibilityType: record.responsibilityType,
        title: record.title,
        description: record.description || '',
        ownerType: record.ownerType,
        ownerUsername: record.ownerUsername || '',
        ownerTeam: record.ownerTeam || '',
        ownerContact: record.ownerContact || '',
        primaryValidatorUsername: record.primaryValidatorUsername || '',
        backupValidatorUsername: record.backupValidatorUsername || '',
        makerUsername: record.makerUsername,
        checkerUsername: record.checkerUsername,
        scopeType: record.scopeType,
        scopeReference: record.scopeReference || '',
        effectiveFrom: dateInput(record.effectiveFrom),
        effectiveTo: dateInput(record.effectiveTo),
        retentionRules: Array.isArray(record.retentionConfiguration)
          ? record.retentionConfiguration.map((rule) => ({ ...rule }))
          : [],
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        makerUsername: makerIdentities.find((identity) => sameIdentity(identity.username, userName))?.username || '',
        checkerUsername: checkerIdentities[0]?.username || '',
      });
    }
    setEditing(record);
  };

  const save = async () => {
    setLoading(true);
    try {
      const input = formInput(form);
      if (editing) await updateBusinessGovernanceOwnership(editing.recordKey, input, token);
      else await createBusinessGovernanceOwnership(input, token);
      setEditing(undefined);
      await load();
      setAlert({
        type: 'success',
        title: editing ? 'Governance draft updated' : 'Governance draft created',
        message: 'The record remains inactive. Its designated maker must submit it and its designated checker must approve it.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save Governance & Ownership record', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const submit = async (record: BusinessGovernanceOwnership) => {
    if (!window.confirm(`Submit ${record.recordKey} to ${record.checkerUsername} for approval?`)) return;
    setLoading(true);
    try {
      await submitBusinessGovernanceOwnership(record.recordKey, token);
      await load();
      setAlert({ type: 'success', title: 'Submitted for approval', message: `${record.recordKey} is awaiting its designated checker.` });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not submit record', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const recordDecision = async () => {
    if (!decision) return;
    setLoading(true);
    try {
      if (decision.kind === 'approve') {
        const reference = cleanSpaces(decision.reference);
        if (!reference) throw new Error('Approval reference is required.');
        await approveBusinessGovernanceOwnership(decision.record.recordKey, {
          approvalReference: reference,
          approvalNotes: cleanSpaces(decision.notes) || null,
        }, token);
      } else {
        const reason = cleanSpaces(decision.reason);
        if (!reason) throw new Error('Rejection reason is required.');
        await rejectBusinessGovernanceOwnership(decision.record.recordKey, reason, token);
      }
      const wasApproval = decision.kind === 'approve';
      setDecision(null);
      await load();
      setAlert({
        type: 'success',
        title: wasApproval ? 'Governance record approved' : 'Governance record rejected',
        message: wasApproval
          ? 'Approval evidence is recorded. Activation remains a separate checker action.'
          : 'The maker can revise and resubmit the rejected record.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not record decision', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (record: BusinessGovernanceOwnership) => {
    const nextActive = !record.isActive;
    if (!window.confirm(`${nextActive ? 'Activate' : 'Deactivate'} ${record.recordKey}? This action is written to the mandatory audit history.`)) return;
    setLoading(true);
    try {
      await setBusinessGovernanceOwnershipActive(record.recordKey, nextActive, token);
      await load();
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not change activation status', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async (record: BusinessGovernanceOwnership) => {
    setLoading(true);
    try {
      const events = await getBusinessGovernanceOwnershipHistory(record.recordKey, token);
      setHistory(events);
      setHistoryRecord(record);
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load audit history', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const addRetentionRule = () => {
    setForm((current) => ({
      ...current,
      retentionRules: [
        ...current.retentionRules,
        { recordType: '', retentionDays: 365, disposition: 'ARCHIVE' },
      ],
    }));
  };

  const updateRetentionRule = (index: number, patch: Partial<BusinessGovernanceRetentionRule>) => {
    setForm((current) => ({
      ...current,
      retentionRules: current.retentionRules.map((rule, ruleIndex) => (
        ruleIndex === index ? { ...rule, ...patch } : rule
      )),
    }));
  };

  return (
    <section className="tab-content business-source-page" id="tab-business-governance-ownership">
      {loading && <Loader message="Processing Governance & Ownership..." />}
      <div className="business-page-toolbar">
        {canCreate && <button className="btn btn-primary" onClick={() => openEditor(null)}>Add Governance Record</button>}
      </div>

      <div className={`business-finance-policy-banner ${businessDefinitionOwnerIsAuthoritative ? 'success' : 'warning'}`}>
        <strong>{businessDefinitionOwnerIsAuthoritative ? 'BR-0-01 is authoritative' : 'BR-0-01 requires governance completion'}</strong>
        <span>
          {businessDefinitionOwnerIsAuthoritative
            ? `The Business Reports definition owner is approved and active under ${businessDefinitionOwner?.approvalReference}.`
            : businessDefinitionOwner
              ? `Current state: ${label(businessDefinitionOwner.status)} and ${businessDefinitionOwner.isActive ? 'active' : 'inactive'}. Maker submission, checker approval evidence and explicit activation are required.`
              : 'The Business Reports definition-owner record is missing and must be created through the maker-checker workflow.'}
        </span>
      </div>

      <div className="business-finance-kpis governance-ownership-kpis">
        <div><span>Total records</span><strong>{counts.total}</strong></div>
        <div><span>Pending approval</span><strong>{counts.pending}</strong></div>
        <div><span>Approved</span><strong>{counts.approved}</strong></div>
        <div><span>Active</span><strong>{counts.active}</strong></div>
      </div>

      <div className="enterprise-card enterprise-table-card governance-register-card">
        <div className="enterprise-card-heading">
          <div><strong>Ownership and responsibility register</strong><span>Server-calculated capabilities ensure only the designated maker or checker sees a valid workflow action.</span></div>
        </div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table governance-register-table">
            <thead>
              <tr><th>Record</th><th>Owner</th><th>Scope</th><th>Maker / Checker</th><th>Version</th><th>Approval</th><th>Activation</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  <td><strong>{record.title}</strong><small>{record.recordKey} · {label(record.responsibilityType)}</small></td>
                  <td><strong>{record.ownerType === 'TEAM' ? record.ownerTeam : record.ownerUsername}</strong><small>{record.ownerUsername || record.ownerContact || 'No representative account'}</small></td>
                  <td><strong>{label(record.scopeType)}</strong><small>{record.scopeReference || 'All Business Reports'}</small></td>
                  <td><strong>{record.makerUsername}</strong><small>Checker: {record.checkerUsername}</small></td>
                  <td>{record.version}</td>
                  <td><FinanceStatus value={record.status} /><small>{record.approvalReference || record.rejectionReason || 'No approval evidence'}</small></td>
                  <td><FinanceStatus value={record.isActive ? 'ACTIVE' : 'INACTIVE'} /><small>{dateRange(record.effectiveFrom, record.effectiveTo)}</small></td>
                  <td>
                    <div className="business-finance-row-actions governance-register-actions">
                      <button className="btn btn-sm governance-action is-history" onClick={() => void openHistory(record)}>History</button>
                      {record.capabilities.canEdit && <button className="btn btn-sm governance-action is-edit" onClick={() => openEditor(record)}>Edit</button>}
                      {record.capabilities.canSubmit && <button className="btn btn-sm governance-action is-submit" onClick={() => void submit(record)}>Submit</button>}
                      {record.capabilities.canApprove && <button className="btn btn-sm governance-action is-approve" onClick={() => setDecision({ kind: 'approve', record, reference: '', notes: '' })}>Approve</button>}
                      {record.capabilities.canReject && <button className="btn btn-sm governance-action is-reject" onClick={() => setDecision({ kind: 'reject', record, reason: '' })}>Reject</button>}
                      {(record.capabilities.canActivate || record.capabilities.canDeactivate) && (
                        <button className={`btn btn-sm governance-action ${record.isActive ? 'is-deactivate' : 'is-activate'}`} onClick={() => void toggleActive(record)}>
                          {record.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!records.length && <tr><td colSpan={8} className="empty-cell">No Governance &amp; Ownership records exist.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing !== undefined && (
        <FinanceModal
          title={editing ? `Edit ${editing.recordKey}` : 'Add Governance & Ownership Record'}
          subtitle="Saving produces an inactive draft and clears all prior approval evidence."
          wide
          onClose={() => setEditing(undefined)}
          footer={<><span>Maker and checker identities are enforced by the API.</span><div><button className="btn btn-outline" onClick={() => setEditing(undefined)}>Cancel</button><button className="btn btn-primary" onClick={() => void save()}>Save Draft</button></div></>}
        >
          <div className="business-source-form-grid">
            <FinanceField label="Record Key"><input value={form.recordKey} disabled={Boolean(editing)} placeholder="BR-0-02" onChange={(event) => setForm((current) => ({ ...current, recordKey: event.target.value.toUpperCase() }))} /></FinanceField>
            <FinanceField label="Responsibility Type"><select value={form.responsibilityType} onChange={(event) => setForm((current) => ({ ...current, responsibilityType: event.target.value as BusinessGovernanceResponsibilityType }))}>{RESPONSIBILITY_TYPES.map((value) => <option key={value} value={value}>{label(value)}</option>)}</select></FinanceField>
            <FinanceField label="Title" full><input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} /></FinanceField>
            <FinanceField label="Description" full><textarea rows={3} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} /></FinanceField>
            <FinanceField label="Owner Type"><select value={form.ownerType} onChange={(event) => setForm((current) => ({ ...current, ownerType: event.target.value as BusinessGovernanceOwnerType, ownerTeam: event.target.value === 'USER' ? '' : current.ownerTeam }))}><option value="TEAM">Team</option><option value="USER">User</option></select></FinanceField>
            {form.ownerType === 'TEAM' && <FinanceField label="Owner Team"><input value={form.ownerTeam} onChange={(event) => setForm((current) => ({ ...current, ownerTeam: event.target.value }))} /></FinanceField>}
            <FinanceField label={form.ownerType === 'TEAM' ? 'Representative Account' : 'Owner Account'}>
              <IdentitySelect value={form.ownerUsername} identities={identities} allowEmpty={form.ownerType === 'TEAM'} onChange={(ownerUsername) => setForm((current) => ({ ...current, ownerUsername }))} />
            </FinanceField>
            <FinanceField label="Owner Contact"><input value={form.ownerContact} placeholder="Email, group mailbox, or contact reference" onChange={(event) => setForm((current) => ({ ...current, ownerContact: event.target.value }))} /></FinanceField>
            <FinanceField label="Primary Validator"><IdentitySelect value={form.primaryValidatorUsername} identities={identities} allowEmpty onChange={(primaryValidatorUsername) => setForm((current) => ({ ...current, primaryValidatorUsername }))} /></FinanceField>
            <FinanceField label="Backup Validator"><IdentitySelect value={form.backupValidatorUsername} identities={identities} allowEmpty onChange={(backupValidatorUsername) => setForm((current) => ({ ...current, backupValidatorUsername }))} /></FinanceField>
            <FinanceField label="Maker"><IdentitySelect value={form.makerUsername} identities={makerIdentities} onChange={(makerUsername) => setForm((current) => ({ ...current, makerUsername }))} /></FinanceField>
            <FinanceField label="Checker"><IdentitySelect value={form.checkerUsername} identities={checkerIdentities} onChange={(checkerUsername) => setForm((current) => ({ ...current, checkerUsername }))} /></FinanceField>
            <FinanceField label="Scope Type"><select value={form.scopeType} onChange={(event) => setForm((current) => ({ ...current, scopeType: event.target.value as BusinessGovernanceScopeType, scopeReference: event.target.value === 'ORGANIZATION' ? '' : current.scopeReference }))}>{SCOPE_TYPES.map((value) => <option key={value} value={value}>{label(value)}</option>)}</select></FinanceField>
            <FinanceField label="Scope Reference" help={form.scopeType === 'ORGANIZATION' ? 'Organization scope applies across Business Reports.' : 'Enter the controlled centre, report, or source key.'}><input value={form.scopeReference} disabled={form.scopeType === 'ORGANIZATION'} onChange={(event) => setForm((current) => ({ ...current, scopeReference: event.target.value }))} /></FinanceField>
            <FinanceField label="Effective From"><input type="date" value={form.effectiveFrom} onChange={(event) => setForm((current) => ({ ...current, effectiveFrom: event.target.value }))} /></FinanceField>
            <FinanceField label="Effective To"><input type="date" value={form.effectiveTo} onChange={(event) => setForm((current) => ({ ...current, effectiveTo: event.target.value }))} /></FinanceField>

            <div className="full" style={{ display: 'grid', gap: 12, padding: 14, border: '1px solid var(--border)', borderRadius: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div><strong>Retention rules</strong><small className="business-finance-subline">Rules remain empty until approved durations are known. Retention-policy records require at least one rule.</small></div>
                <button type="button" className="btn btn-outline btn-sm" onClick={addRetentionRule}>Add Rule</button>
              </div>
              {form.retentionRules.map((rule, index) => (
                <div key={`${index}-${rule.recordType}`} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.2fr auto', gap: 8, alignItems: 'end' }}>
                  <FinanceField label="Record Type"><input value={rule.recordType} placeholder="REPORT_EXPORT" onChange={(event) => updateRetentionRule(index, { recordType: event.target.value.toUpperCase() })} /></FinanceField>
                  <FinanceField label="Retention Days"><input type="number" min={1} max={36500} value={rule.retentionDays} onChange={(event) => updateRetentionRule(index, { retentionDays: Number(event.target.value) })} /></FinanceField>
                  <FinanceField label="Archive After"><input type="number" min={1} value={rule.archiveAfterDays ?? ''} onChange={(event) => updateRetentionRule(index, { archiveAfterDays: event.target.value ? Number(event.target.value) : undefined })} /></FinanceField>
                  <FinanceField label="Disposition"><select value={rule.disposition} onChange={(event) => updateRetentionRule(index, { disposition: event.target.value as BusinessGovernanceRetentionRule['disposition'] })}><option value="ARCHIVE">Archive</option><option value="DELETE">Delete</option><option value="ANONYMIZE">Anonymize</option></select></FinanceField>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setForm((current) => ({ ...current, retentionRules: current.retentionRules.filter((_, ruleIndex) => ruleIndex !== index) }))}>Remove</button>
                </div>
              ))}
              {!form.retentionRules.length && <span className="business-finance-subline">No retention durations have been approved.</span>}
            </div>
          </div>
        </FinanceModal>
      )}

      {decision?.kind === 'approve' && (
        <FinanceModal title={`Approve ${decision.record.recordKey}`} subtitle="The authenticated checker and approval evidence are stored transactionally." onClose={() => setDecision(null)} footer={<><span>Activation remains separate.</span><div><button className="btn btn-outline" onClick={() => setDecision(null)}>Cancel</button><button className="btn btn-primary" onClick={() => void recordDecision()}>Approve</button></div></>}>
          <div style={{ display: 'grid', gap: 14 }}>
            <FinanceField label="Approval Reference"><input value={decision.reference} onChange={(event) => setDecision({ ...decision, reference: event.target.value })} placeholder="Approved document, ticket, or decision ID" /></FinanceField>
            <FinanceField label="Approval Notes"><textarea rows={4} value={decision.notes} onChange={(event) => setDecision({ ...decision, notes: event.target.value })} /></FinanceField>
          </div>
        </FinanceModal>
      )}

      {decision?.kind === 'reject' && (
        <FinanceModal title={`Reject ${decision.record.recordKey}`} subtitle="A reason is mandatory and retained in audit history." onClose={() => setDecision(null)} footer={<><span>The maker may revise and resubmit.</span><div><button className="btn btn-outline" onClick={() => setDecision(null)}>Cancel</button><button className="btn btn-primary" onClick={() => void recordDecision()}>Reject</button></div></>}>
          <FinanceField label="Rejection Reason"><textarea rows={4} value={decision.reason} onChange={(event) => setDecision({ ...decision, reason: event.target.value })} /></FinanceField>
        </FinanceModal>
      )}

      {historyRecord && (
        <FinanceModal title={`${historyRecord.recordKey} Audit History`} subtitle="Append-only database snapshots and semantic workflow events." wide onClose={() => { setHistoryRecord(null); setHistory([]); }} footer={<><span>{history.length} retained event{history.length === 1 ? '' : 's'}</span><button className="btn btn-outline" onClick={() => { setHistoryRecord(null); setHistory([]); }}>Close</button></>}>
          <div className="enterprise-table-wrap">
            <table className="enterprise-table">
              <thead><tr><th>Time</th><th>Action</th><th>Actor</th><th>State</th><th>Version</th></tr></thead>
              <tbody>
                {history.map((event) => (
                  <tr key={event.id}>
                    <td>{dateTime(event.createdAt)}</td>
                    <td><strong>{label(event.action.replace(/^business_governance_ownership\./, ''))}</strong><small>{event.action}</small></td>
                    <td>{event.actor}</td>
                    <td>{String(event.afterState?.status || event.beforeState?.status || '—')}</td>
                    <td>{String(event.afterState?.version || event.beforeState?.version || '—')}</td>
                  </tr>
                ))}
                {!history.length && <tr><td colSpan={5} className="empty-cell">No audit history exists.</td></tr>}
              </tbody>
            </table>
          </div>
        </FinanceModal>
      )}

      {alert && <AdminAlertModal title={alert.title} message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
    </section>
  );
}

function IdentitySelect({
  value,
  identities,
  allowEmpty = false,
  onChange,
}: {
  value: string;
  identities: BusinessGovernanceIdentity[];
  allowEmpty?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      {allowEmpty && <option value="">Not assigned</option>}
      {!allowEmpty && !value && <option value="">Select account</option>}
      {identities.map((identity) => (
        <option key={identity.id} value={identity.username}>
          {identity.displayName ? `${identity.displayName} — ` : ''}{identity.username}
        </option>
      ))}
    </select>
  );
}

function formInput(form: OwnershipForm): BusinessGovernanceOwnershipInput {
  const recordKey = cleanSpaces(form.recordKey).toUpperCase();
  const title = cleanSpaces(form.title);
  const makerUsername = cleanSpaces(form.makerUsername).toLowerCase();
  const checkerUsername = cleanSpaces(form.checkerUsername).toLowerCase();
  const ownerUsername = cleanSpaces(form.ownerUsername).toLowerCase();
  const ownerTeam = cleanSpaces(form.ownerTeam);
  const ownerContact = cleanSpaces(form.ownerContact);
  if (!recordKey || !title || !makerUsername || !checkerUsername) {
    throw new Error('Record key, title, maker and checker are required.');
  }
  if (sameIdentity(makerUsername, checkerUsername)) {
    throw new Error('Maker and checker must be different users.');
  }
  if (form.ownerType === 'USER' && !ownerUsername) {
    throw new Error('Select the owner account.');
  }
  if (form.ownerType === 'TEAM' && !ownerTeam) {
    throw new Error('Owner team is required.');
  }
  if (form.scopeType !== 'ORGANIZATION' && !cleanSpaces(form.scopeReference)) {
    throw new Error(`${label(form.scopeType)} scope requires a reference.`);
  }
  if (form.effectiveFrom && form.effectiveTo && form.effectiveTo < form.effectiveFrom) {
    throw new Error('Effective To must be on or after Effective From.');
  }
  return {
    recordKey,
    responsibilityType: form.responsibilityType,
    title,
    description: cleanSpaces(form.description) || null,
    ownerType: form.ownerType,
    ownerUsername: ownerUsername || null,
    ownerTeam: form.ownerType === 'TEAM' ? ownerTeam : null,
    ownerContact: ownerContact || null,
    primaryValidatorUsername: cleanSpaces(form.primaryValidatorUsername).toLowerCase() || null,
    backupValidatorUsername: cleanSpaces(form.backupValidatorUsername).toLowerCase() || null,
    makerUsername,
    checkerUsername,
    scopeType: form.scopeType,
    scopeReference: form.scopeType === 'ORGANIZATION' ? null : cleanSpaces(form.scopeReference),
    effectiveFrom: form.effectiveFrom || null,
    effectiveTo: form.effectiveTo || null,
    retentionRules: form.retentionRules.map((rule) => ({
      recordType: cleanSpaces(rule.recordType).toUpperCase().replace(/[^A-Z0-9]+/g, '_'),
      retentionDays: Number(rule.retentionDays),
      ...(rule.archiveAfterDays == null ? {} : { archiveAfterDays: Number(rule.archiveAfterDays) }),
      disposition: rule.disposition,
    })),
  };
}

function dateInput(value: string | null | undefined) {
  return value ? value.slice(0, 10) : '';
}

function dateRange(from: string | null, to: string | null) {
  if (!from && !to) return 'Effective dates pending';
  return `${from?.slice(0, 10) || 'Open'} → ${to?.slice(0, 10) || 'Open'}`;
}

function sameIdentity(left: string | null | undefined, right: string | null | undefined) {
  return Boolean(left && right && left.trim().toLowerCase() === right.trim().toLowerCase());
}
